import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const isCapacitor = Capacitor.isNativePlatform();

const get = (key) => {
  try {
    if (isCapacitor) {
      // Try localStorage first (sync cache)
      try {
        const value = localStorage.getItem(key);

        if (value !== null) {
          return value;
        }

        // If not in localStorage, this is likely first access - data might be in Preferences
        // Since we can't do async here, return null and let the app use async getAsync if needed
        return null;
      } catch (localStorageError) {
        console.warn(`Capacitor: localStorage fallback failed for key "${key}":`, localStorageError);

        return null;
      }
    } else {
      return localStorage.getItem(key);
    }
  } catch (error) {
    console.error(`Error getting storage key "${key}":`, error);

    return null;
  }
};

// Async version for Capacitor to properly sync from Preferences on first load
const getAsync = async (key) => {
  try {
    if (isCapacitor) {
      // Check localStorage first
      try {
        const localValue = localStorage.getItem(key);

        if (localValue !== null) {
          return localValue;
        }
      } catch (localStorageError) {
        console.warn(`Capacitor: localStorage check failed for key "${key}":`, localStorageError);
      }

      // If not in localStorage, check Preferences
      const { value } = await Preferences.get({ key });

      if (value !== null) {
        // Sync back to localStorage for faster future access
        try {
          localStorage.setItem(key, value);
        } catch (e) {
          console.warn(`Failed to sync "${key}" to localStorage:`, e);
        }
      }

      return value;
    } else {
      return localStorage.getItem(key);
    }
  } catch (error) {
    console.error(`Error getting storage key "${key}" async:`, error);

    return null;
  }
};

const set = (key, value) => {
  try {
    if (isCapacitor) {
      // For Capacitor, try Preferences first (primary storage), then localStorage as fallback
      Preferences.set({ key, value: String(value) })
        .then(() => {
          // Also update localStorage as a sync cache
          try {
            localStorage.setItem(key, value);
          } catch (localStorageError) {
            console.warn(`Capacitor: localStorage cache update failed for key "${key}":`, localStorageError);
          }
        })
        .catch(error => {
          console.error(`Error setting storage key "${key}" in Preferences:`, error);
          // Fallback to localStorage if Preferences fails
          try {
            localStorage.setItem(key, value);
          } catch (localStorageError) {
            console.error(`Capacitor: Both Preferences and localStorage failed for key "${key}":`, localStorageError);
          }
        });
    } else {
      localStorage.setItem(key, value);
    }
  } catch (error) {
    console.error(`Error setting storage key "${key}":`, error);
  }
};

// Async version for better control
const setAsync = async (key, value) => {
  try {
    if (isCapacitor) {
      // Try Preferences first
      try {
        await Preferences.set({ key, value: String(value) });
        // Also update localStorage as a sync cache
        try {
          localStorage.setItem(key, value);
        } catch (localStorageError) {
          console.warn(`Capacitor: localStorage cache update failed for key "${key}":`, localStorageError);
        }
      } catch (preferencesError) {
        console.error(`Error setting storage key "${key}" in Preferences:`, preferencesError);
        // Fallback to localStorage if Preferences fails
        localStorage.setItem(key, value);
      }
    } else {
      localStorage.setItem(key, value);
    }
  } catch (error) {
    console.error(`Error setting storage key "${key}" async:`, error);
    throw error;
  }
};

const remove = (key) => {
  try {
    if (isCapacitor) {
      // Remove from localStorage cache
      try {
        localStorage.removeItem(key);
      } catch (localStorageError) {
        console.warn(`Capacitor: localStorage cache removal failed for key "${key}":`, localStorageError);
      }
      // Also remove from Capacitor Preferences asynchronously
      Preferences.remove({ key }).catch(error => {
        console.error(`Error removing storage key "${key}":`, error);
      });
    } else {
      localStorage.removeItem(key);
    }
  } catch (error) {
    console.error(`Error removing storage key "${key}":`, error);
  }
};


const clear = () => {
  try {
    if (isCapacitor) {
      // Clear localStorage cache
      try {
        localStorage.clear();
      } catch (localStorageError) {
        console.warn(`Capacitor: localStorage cache clear failed:`, localStorageError);
      }
      // Also clear Capacitor Preferences asynchronously
      Preferences.clear().catch(error => {
        console.error('Error clearing storage:', error);
      });
    } else {
      localStorage.clear();
    }
  } catch (error) {
    console.error('Error clearing storage:', error);
  }
};

const getMultiple = (keys) => {
  try {
    const values = {};

    keys.forEach(key => {
      values[key] = get(key);
    });

    return values;
  } catch (error) {
    console.error('Error getting multiple storage keys:', error);

    return {};
  }
};


const setMultiple = (items) => {
  try {
    Object.entries(items).forEach(([key, value]) => {
      set(key, value);
    });
  } catch (error) {
    console.error('Error setting multiple storage keys:', error);
  }
};

const setMultipleAsync = async (item) => {
  try {
    await Promise.all(
      Object.entries(item).map(([key, value]) => set(key, value))
    );
  } catch (error) {
    console.error('Error setting multiple storage keys:', error);
  }
};

const storage = {
  get,
  getAsync,
  set,
  setAsync,
  remove,
  clear,
  getMultiple,
  setMultiple,
  setMultipleAsync
};

export default storage;